import React, { useMemo, useState } from 'react';
import { Table, Spinner, Badge, FormControl, Button, Modal, Form } from 'react-bootstrap';
import classNames from 'classnames';
import { ICrossPair, IRate, LegInfo } from './types';
import ApiService from 'helpers/apiService';

interface RateInfo { price: number | string; valid?: boolean; created_at: string }

type Props = {
  data: ICrossPair[];
  loading: boolean;
  legs: LegInfo[]; // лиги для читаемых подписей и пикера в редактировании
  ratesBySymbol?: Record<string, RateInfo>; // текущие курсы (перенесено из «Доступные валюты»)
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  onRefresh?: () => void;
};

const fmtRateTime = (s?: string): string => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString();
};
const fmtPrice = (p: number | string): string =>
  typeof p === 'number' ? p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : String(p);

const PairsTable = ({ data, loading, legs, ratesBySymbol = {}, sortKey, sortDir, onSort, onRefresh }: Props) => {
  const rateFor = (symbol: string): RateInfo | undefined =>
    ratesBySymbol[(symbol || '').replace(/"/g, '').trim().toUpperCase()];

  // Кликабельный сортируемый заголовок (стрелка показывает направление).
  const SortTh = ({ k, label, className }: { k: string; label: string; className?: string }) => (
    <th
      className={className}
      style={{ cursor: onSort ? 'pointer' : undefined, whiteSpace: 'nowrap' }}
      onClick={() => onSort?.(k)}
      title="Сортировать"
    >
      {label}{sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<'deviation_limit' | 'discrepancy_limit' | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const legById = useMemo(() => {
    const m = new Map<number, LegInfo>();
    legs.forEach(l => m.set(l.pairId, l));
    return m;
  }, [legs]);

  const legLabel = (pairId: number): string => {
    const l = legById.get(pairId);
    return l ? `${l.symbol} · ${l.provider}` : `#${pairId}`;
  };

  const handleLimitEdit = (id: number, field: 'deviation_limit' | 'discrepancy_limit', value: number) => {
    setEditingId(id);
    setEditingField(field);
    setEditValue(value.toString());
  };

  const handleLimitSave = async (pair: ICrossPair) => {
    if (!editingField) return;
    const updatedPair: ICrossPair = {
      ...pair,
      [editingField]: parseFloat(editValue),
      reliable_source: pair.reliable_source
    };
    try {
      await ApiService.putCrossPairs(updatedPair);
    } catch (e) {
      console.error('Ошибка обновления:', e);
    }
    setEditingId(null);
    setEditingField(null);
    setEditValue('');
    onRefresh?.();
  };

  const handleStatusToggle = async (pair: ICrossPair) => {
    try {
      await ApiService.putCrossPairs({ ...pair, active: !pair.active });
      onRefresh?.();
    } catch (e) {
      console.error('Ошибка обновления статуса:', e);
    }
  };

  const handleDelete = async (pair: ICrossPair) => {
    if (!window.confirm(`Удалить кросс-пару ${pair.symbol}?`)) return;
    try {
      await ApiService.deleteCrossPair(pair.id);
      onRefresh?.();
    } catch (e) {
      console.error('Ошибка удаления кросс-пары:', e);
    }
  };

  // ── Модалка полного редактирования источников ──
  const [editPair, setEditPair] = useState<ICrossPair | null>(null);
  const [mReliable, setMReliable] = useState<number | ''>('');
  const [mCorrective, setMCorrective] = useState<number[]>([]);
  const [mStabilizer, setMStabilizer] = useState<number[]>([]);
  const [mCorrectivePick, setMCorrectivePick] = useState<number | ''>('');
  const [mStabilizerPick, setMStabilizerPick] = useState<number | ''>('');
  const [mDeviation, setMDeviation] = useState('');
  const [mDiscrepancy, setMDiscrepancy] = useState('');
  const [mSaving, setMSaving] = useState(false);
  const [mError, setMError] = useState<string | null>(null);

  const openEdit = (pair: ICrossPair) => {
    setEditPair(pair);
    setMReliable(pair.reliable_source?.pair_id ?? '');
    setMCorrective((pair.corrective_rates ?? []).map(r => r.pair_id));
    setMStabilizer((pair.stabilizer_rates ?? []).map(r => r.pair_id));
    setMCorrectivePick('');
    setMStabilizerPick('');
    setMDeviation(String(pair.deviation_limit ?? ''));
    setMDiscrepancy(String(pair.discrepancy_limit ?? ''));
    setMError(null);
  };

  const sortedLegs = useMemo(
    () => [...legs].sort((a, b) => a.symbol.localeCompare(b.symbol) || a.provider.localeCompare(b.provider)),
    [legs]
  );

  const handleEditSave = async () => {
    if (!editPair) return;
    if (mReliable === '') { setMError('Выберите надёжный источник.'); return; }

    // type-fallback из исходных связей, если лига не найдена в legById (CORS/неполный список)
    const origByPairId = new Map<number, IRate>();
    [editPair.reliable_source, ...(editPair.corrective_rates ?? []), ...(editPair.stabilizer_rates ?? [])]
      .filter(Boolean)
      .forEach(r => origByPairId.set(r.pair_id, r));

    const toPut = (pairId: number): IRate => ({
      cross_id: editPair.id,
      pair_id: pairId,
      type: legById.get(pairId)?.provider || origByPairId.get(pairId)?.type || '',
      status: 'active',
    });

    const payload: ICrossPair = {
      ...editPair,
      deviation_limit: Number(mDeviation) || 0,
      discrepancy_limit: Number(mDiscrepancy) || 0,
      reliable_source: toPut(mReliable),
      corrective_rates: mCorrective.map(toPut),
      stabilizer_rates: mStabilizer.map(toPut),
    };

    setMSaving(true);
    setMError(null);
    try {
      await ApiService.putCrossPairs(payload);
      setEditPair(null);
      onRefresh?.();
    } catch (e) {
      console.error('Ошибка сохранения кросс-пары:', e);
      setMError('Ошибка сохранения (см. консоль).');
    } finally {
      setMSaving(false);
    }
  };

  const editOptions = (exclude: number[]) =>
    sortedLegs.filter(l => !exclude.includes(l.pairId));

  const formatRelations = (rates: IRate[] | null) => {
    if (rates === null) return <span className="text-muted">Не настроено</span>;
    if (!rates.length) return <span className="text-warning">Нет данных</span>;
    return rates.map(r => (
      <Badge bg="light" text="dark" className="border me-1 mb-1" key={r.pair_id}>{legLabel(r.pair_id)}</Badge>
    ));
  };

  if (loading) return <Spinner animation="border" />;
  if (!data.length) return <p className="text-muted mb-0">Кросс-пары не найдены</p>;

  return (
    <>
      <Table hover responsive size="sm" className="align-middle mb-0">
        <thead className="table-light">
          <tr>
            <SortTh k="id" label="ID" />
            <SortTh k="symbol" label="Пара" />
            <SortTh k="active" label="Статус" />
            <SortTh k="price" label="Курс" />
            <SortTh k="valid" label="Статус курса" />
            <SortTh k="received" label="Получен" />
            <th>Надёжный источник</th>
            <SortTh k="deviation_limit" label="Лимит отклонения" />
            <SortTh k="discrepancy_limit" label="Лимит расхождения" />
            <th>Корректирующие</th>
            <th>Стабилизирующие</th>
            <th className="text-end">Действия</th>
          </tr>
        </thead>
        <tbody>
          {data.map(pair => (
            <tr key={pair.id}>
              <td>{pair.id}</td>
              <td><Badge bg="secondary">{pair.symbol}</Badge></td>
              <td onDoubleClick={() => handleStatusToggle(pair)} style={{ cursor: 'pointer' }} title="Двойной клик — переключить">
                <Badge bg={pair.active ? 'success' : 'danger'}>{pair.active ? 'Активна' : 'Неактивна'}</Badge>
              </td>
              {/* Курс / статус курса / время — перенесено из вкладки «Доступные валюты» */}
              {(() => {
                const r = rateFor(pair.symbol);
                return (
                  <>
                    <td>{r ? fmtPrice(r.price) : <span className="text-muted">—</span>}</td>
                    <td>{r && r.valid !== undefined
                      ? <Badge bg={r.valid ? 'success' : 'secondary'}>{r.valid ? 'Актуален' : 'Устарел'}</Badge>
                      : <span className="text-muted">—</span>}</td>
                    <td className="text-muted small">{r ? fmtRateTime(r.created_at) : '—'}</td>
                  </>
                );
              })()}
              <td>{pair.reliable_source ? <Badge bg="light" text="dark" className="border">{legLabel(pair.reliable_source.pair_id)}</Badge> : <span className="text-warning">—</span>}</td>
              <td onDoubleClick={() => handleLimitEdit(pair.id, 'deviation_limit', pair.deviation_limit)} title="Двойной клик — изменить">
                {editingId === pair.id && editingField === 'deviation_limit' ? (
                  <FormControl
                    autoFocus size="sm" value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onBlur={() => handleLimitSave(pair)}
                    onKeyDown={e => { if (e.key === 'Enter') handleLimitSave(pair); if (e.key === 'Escape') setEditingId(null); }}
                  />
                ) : `${pair.deviation_limit.toFixed(2)}%`}
              </td>
              <td onDoubleClick={() => handleLimitEdit(pair.id, 'discrepancy_limit', pair.discrepancy_limit)} title="Двойной клик — изменить">
                {editingId === pair.id && editingField === 'discrepancy_limit' ? (
                  <FormControl
                    autoFocus size="sm" value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onBlur={() => handleLimitSave(pair)}
                    onKeyDown={e => { if (e.key === 'Enter') handleLimitSave(pair); if (e.key === 'Escape') setEditingId(null); }}
                  />
                ) : `${pair.discrepancy_limit.toFixed(2)}%`}
              </td>
              <td className={classNames({ 'text-muted': !pair.corrective_rates })}>{formatRelations(pair.corrective_rates)}</td>
              <td className={classNames({ 'text-muted': !pair.stabilizer_rates })}>{formatRelations(pair.stabilizer_rates)}</td>
              <td className="text-end">
                <div className="d-flex gap-1 justify-content-end">
                  <Button variant="outline-secondary" size="sm" onClick={() => openEdit(pair)}>Изменить</Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDelete(pair)}>Удалить</Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Модалка редактирования источников */}
      <Modal show={!!editPair} onHide={() => setEditPair(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Изменить кросс-пару {editPair?.symbol}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {legs.length === 0 && (
            <div className="alert alert-warning py-2 small">
              Список пар провайдеров пуст (CORS/недоступность сервиса курсов) — выбор источников недоступен,
              можно изменить только лимиты.
            </div>
          )}
          {mError && <div className="text-danger small mb-2">{mError}</div>}
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label className="small">Лимит отклонения</Form.Label>
              <Form.Control size="sm" type="number" step="any" value={mDeviation} onChange={e => setMDeviation(e.target.value)} />
            </div>
            <div className="col-md-6">
              <Form.Label className="small">Лимит расхождения</Form.Label>
              <Form.Control size="sm" type="number" step="any" value={mDiscrepancy} onChange={e => setMDiscrepancy(e.target.value)} />
            </div>

            <div className="col-md-12">
              <Form.Label className="small">Надёжный источник *</Form.Label>
              <Form.Select size="sm" value={mReliable} onChange={e => setMReliable(e.target.value ? Number(e.target.value) : '')} disabled={!legs.length}>
                <option value="">— выберите пару —</option>
                {editOptions(mCorrective.concat(mStabilizer)).map(l => (
                  <option key={l.pairId} value={l.pairId}>{l.symbol} · {l.provider}</option>
                ))}
                {/* текущий источник, если его нет в списке лиг (legById не знает) */}
                {mReliable !== '' && !legById.has(mReliable) && <option value={mReliable}>{legLabel(mReliable)}</option>}
              </Form.Select>
            </div>

            <div className="col-md-6">
              <Form.Label className="small">Корректирующие котировки</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select size="sm" value={mCorrectivePick} onChange={e => setMCorrectivePick(e.target.value ? Number(e.target.value) : '')} disabled={!legs.length}>
                  <option value="">— добавить пару —</option>
                  {editOptions([mReliable, ...mCorrective, ...mStabilizer].filter((x): x is number => x !== '')).map(l => (
                    <option key={l.pairId} value={l.pairId}>{l.symbol} · {l.provider}</option>
                  ))}
                </Form.Select>
                <Button variant="outline-secondary" size="sm" disabled={mCorrectivePick === ''} onClick={() => {
                  if (mCorrectivePick !== '') { setMCorrective(prev => [...prev, mCorrectivePick]); setMCorrectivePick(''); }
                }}>+</Button>
              </div>
              <div className="mt-1">
                {mCorrective.map(id => (
                  <span key={id} className="badge bg-light text-dark border me-1 mb-1">
                    {legLabel(id)}
                    <button type="button" className="btn-close btn-close-sm ms-2" style={{ fontSize: '0.6rem' }} onClick={() => setMCorrective(prev => prev.filter(x => x !== id))} />
                  </span>
                ))}
              </div>
            </div>

            <div className="col-md-6">
              <Form.Label className="small">Стабилизирующие котировки</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select size="sm" value={mStabilizerPick} onChange={e => setMStabilizerPick(e.target.value ? Number(e.target.value) : '')} disabled={!legs.length}>
                  <option value="">— добавить пару —</option>
                  {editOptions([mReliable, ...mCorrective, ...mStabilizer].filter((x): x is number => x !== '')).map(l => (
                    <option key={l.pairId} value={l.pairId}>{l.symbol} · {l.provider}</option>
                  ))}
                </Form.Select>
                <Button variant="outline-secondary" size="sm" disabled={mStabilizerPick === ''} onClick={() => {
                  if (mStabilizerPick !== '') { setMStabilizer(prev => [...prev, mStabilizerPick]); setMStabilizerPick(''); }
                }}>+</Button>
              </div>
              <div className="mt-1">
                {mStabilizer.map(id => (
                  <span key={id} className="badge bg-light text-dark border me-1 mb-1">
                    {legLabel(id)}
                    <button type="button" className="btn-close btn-close-sm ms-2" style={{ fontSize: '0.6rem' }} onClick={() => setMStabilizer(prev => prev.filter(x => x !== id))} />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setEditPair(null)}>Отмена</Button>
          <Button variant="primary" size="sm" onClick={handleEditSave} disabled={mSaving}>
            {mSaving ? <Spinner animation="border" size="sm" className="me-1" /> : null}Сохранить
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PairsTable;
